import { SalesInvoiceDataForm, SalesInvoiceFilters } from '../../../../app/models/SalesInvoice';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import SalesInvoiceFilter from './SalesInvoiceFilter';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';

interface Props {
    salesInvoices: SalesInvoiceDataForm[];
    totalRows: number;
    loading?: boolean;
    edit?: (id: number) => void;
    destroy?: (id: number) => void;
    filter: SalesInvoiceFilters;
    setFilter: (filter: SalesInvoiceFilters) => void;
    callbackSuccess: () => void;
    onSearch: () => void;
}

const SalesInvoiceDataTable = ({
    salesInvoices,
    totalRows,
    loading = false,
    edit,
    destroy,
    filter,
    setFilter,
    callbackSuccess,
    onSearch
}: Props) => {
    const columns = [
        {
            name: 'ID',
            selector: (row: any) => row.id,
            sortable: true,
            sortField: 'id',
            width: '80px',
            cell: (row: SalesInvoiceDataForm) => (
                <span style={{ whiteSpace: 'nowrap' }}>#{row.id}</span>
            )
        },
        {
            name: 'RUT Sociedad',
            selector: (row: any) => row.society_rut,
            sortable: false,
            width: '140px',
            cell: (row: SalesInvoiceDataForm) => (
                <span style={{ whiteSpace: 'nowrap' }}>{row.society_rut || '—'}</span>
            )
        },
        {
            name: 'Sociedad',
            selector: (row: any) => row.society_name,
            sortable: false,
            width: '220px',
            cell: (row: SalesInvoiceDataForm) => (
                <span
                    style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        maxWidth: '215px'
                    }}
                >
                    {row.society_name || '—'}
                </span>
            )
        },
        {
            name: 'Folio',
            selector: (row: any) => row.folio,
            sortable: true,
            sortField: 'folio',
            width: '120px',
            cell: (row: SalesInvoiceDataForm) => (
                <span style={{ whiteSpace: 'nowrap' }}>{row.folio || '—'}</span>
            )
        },
        {
            name: 'Fecha de Emisión',
            selector: (row: any) => row.issue_date,
            sortable: true,
            sortField: 'issue_date',
            width: '130px',
            cell: (row: SalesInvoiceDataForm) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {row.issue_date ? new Date(row.issue_date).toLocaleDateString('es-CL') : '—'}
                </span>
            )
        },
        {
            name: 'RUT Cliente',
            selector: (row: any) => row.client_rut,
            sortable: false,
            width: '140px',
            cell: (row: SalesInvoiceDataForm) => (
                <span style={{ whiteSpace: 'nowrap' }}>{row.client_rut || '—'}</span>
            )
        },
        {
            name: 'Cliente',
            selector: (row: any) => row.client_name,
            sortable: false,
            width: '260px',
            cell: (row: SalesInvoiceDataForm) => (
                <span
                    style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        maxWidth: '255px'
                    }}
                >
                    {row.client_name || '—'}
                </span>
            )
        },
        {
            name: 'Moneda',
            selector: (row: any) => (row as any).currency,
            sortable: false,
            width: '90px',
            cell: (row: SalesInvoiceDataForm) => (
                <span style={{ whiteSpace: 'nowrap' }}>{(row as any).currency || '—'}</span>
            )
        },
        {
            name: 'Total Venta',
            selector: (row: any) => row.total_sale,
            sortable: true,
            sortField: 'total_sale',
            width: '130px',
            cell: (row: SalesInvoiceDataForm) => (
                <span
                    style={{
                        display: 'block',
                        textAlign: 'right',
                        width: '100%',
                        whiteSpace: 'nowrap'
                    }}
                >
                    ${Number(row.total_sale || 0).toLocaleString('es-CL')}
                </span>
            )
        },
        {
            name: 'Estado',
            selector: (row: any) => row.status,
            sortable: true,
            sortField: 'status',
            width: '130px',
            cell: (row: SalesInvoiceDataForm) => {
                const statusColors: any = {
                    'DRAFT': 'bg-secondary text-white',
                    'ACCOUNTED': 'bg-success text-white',
                    'ANNULLED': 'bg-danger text-white',
                    'BORRADOR': 'bg-secondary text-white',
                    'CONTABILIZADA': 'bg-success text-white',
                    'ANULADA': 'bg-danger text-white'
                };
                return (
                    <span className={`badge ${statusColors[row.status || ''] || 'bg-secondary text-white'}`}>
                        {row.status_label || row.status || '-'}
                    </span>
                );
            }
        },
        {
            name: 'Fecha de Creación',
            selector: (row: any) => row.created_at,
            sortable: true,
            sortField: 'created_at',
            width: '130px',
            cell: (row: SalesInvoiceDataForm) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {row.created_at ? new Date(row.created_at).toLocaleDateString('es-CL') : '—'}
                </span>
            )
        },
        {
            name: 'Acciones',
            selector: (row: any) => row?.id,
            sortable: false,
            width: '120px',
            cell: (row: SalesInvoiceDataForm) => (
                <div className="">
                    {edit && (
                        <ButtonTableAction
                            callbackFunction={() => edit(row?.id ?? -1)}
                            classIcon={'mdi mdi-pencil'}
                            colorIcon={'text-warning'}
                            errorMessage={'No se puede editar este registro.'}
                            title={'Editar'}
                        />
                    )}
                    {destroy && (
                        <ButtonTableAction
                            callbackFunction={() => destroy(row?.id ?? -1)}
                            classIcon={'mdi mdi-delete'}
                            colorIcon={'text-danger'}
                            errorMessage={'No se puede eliminar este registro.'}
                            title={'Eliminar'}
                        />
                    )}
                </div>
            )
        }
    ];

    return (
        <>
            <div className="row mt-3">
                <div className="col">
                    <SalesInvoiceFilter 
                        filter={filter} 
                        setFilter={setFilter} 
                        onSearch={onSearch} 
                    />
                </div>
            </div>

            <DefaultDataTable
                columns={columns}
                data={salesInvoices}
                progressPending={loading}
                paginationTotalRows={totalRows}
                filter={filter}
                setFilter={setFilter}
            />
        </>
    );
};

export default SalesInvoiceDataTable;
